import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import ReactHighcharts from 'react-highcharts';
import {injectIntl} from 'react-intl';
import {grey500} from 'material-ui/styles/colors';
import {
  tntBlue,
  tntGreen,
  tntRed,
  tntOrange,
  tntPink,
  tntPurple,
  tntYellow,
  tntTeal,
} from '../../theme/tntColors';
import moment from 'moment';
import _ from 'lodash';

function getCollapseTimePeriodFunction(timePeriod) {
  return (arr, {date, score, count}) => {
    const lastItemTimeMs = arr[arr.length-1] && arr[arr.length-1].x;
    const thisItemTimeMs = moment.utc(date).startOf(timePeriod).valueOf();
    if (lastItemTimeMs === thisItemTimeMs) {
      const lastCount = arr[arr.length-1].count;
      const lastScore = arr[arr.length-1].y;
      arr[arr.length-1].y = ((lastCount*lastScore)+(count*score))/(lastCount+count);
      arr[arr.length-1].count += count;
      return arr;
    } else {
      return arr.concat([{
        x: moment.utc(date).startOf(timePeriod).valueOf(),
        y: score,
        count: count,
      }]);
    }
  };
}

function getData(scoreByDate, collapseTimePeriodTo) {
  if (!Array.isArray(scoreByDate) || scoreByDate.length <= 0) {
    return [];
  } else if (!collapseTimePeriodTo) {
    return scoreByDate.map(({date, score, count}) => {
      return {
        x: Date.parse(date),
        y: score,
        count: count,
      };
    });
  } else {
    return scoreByDate.reduce(getCollapseTimePeriodFunction(collapseTimePeriodTo), []);
  }
}

function getSeriesColor(index) {
  const colors = [
    tntBlue,
    tntOrange,
    tntPurple,
    tntYellow,
    tntPink,
    tntTeal,
    tntGreen,
    tntRed,
    grey500,
  ];
  return colors[index % colors.length];
}

function getSeries(scoreByDeviceAndDate, intl) {
  if (!Array.isArray(scoreByDeviceAndDate) || scoreByDeviceAndDate.length <= 0) {
    return {
      series: [],
      tooltipDateFormatter: () => null,
      xAxisDateFormatter: () => null,
    };
  }

  const {firstDate, lastDate} = scoreByDeviceAndDate
    .map(x => {
      if (Array.isArray(x.scoreByDate) && x.scoreByDate.length > 0) {
        return {
          firstDate: x.scoreByDate[0].date,
          lastDate: x.scoreByDate[x.scoreByDate.length-1].date,
        };
      } else {
        return null;
      }
    })
    .filter(x => !_.isNil(x))
    .reduce((obj, {firstDate, lastDate}) => {
      if (!obj.firstDate || !obj.lastDate) {
        return {firstDate, lastDate};
      } else {
        return {
          firstDate: moment(obj.firstDate).diff(moment(firstDate)) > 0 ? firstDate : obj.firstDate,
          lastDate: moment(lastDate).diff(moment(obj.lastDate)) > 0 ? lastDate : obj.lastDate,
        };
      }
    }, {});

  const monthsCount = moment(lastDate).diff(moment(firstDate), 'months');

  if (monthsCount < 3) {
    return {
      series: scoreByDeviceAndDate.map((x, i) => {
        return {
          name: x.device,
          data: getData(x.scoreByDate),
          color: getSeriesColor(i),
        };
      }),
      tooltipDateFormatter: ms => moment.utc(ms).format('dddd, D MMM Y'),
      xAxisDateFormatter: ms => moment.utc(ms).format('D MMM<br/>Y'),
    };
  } else if (monthsCount < 12) {
    return {
      series: scoreByDeviceAndDate.map((x, i) => {
        return {
          name: x.device,
          data: getData(x.scoreByDate, 'isoWeek'),
          color: getSeriesColor(i),
        };
      }),
      tooltipDateFormatter: ms => {
        return intl.formatMessage({id: 'week'}) + moment.utc(ms).format(' W, Y');
      },
      xAxisDateFormatter: ms => {
        return intl.formatMessage({id: 'week'}) + moment.utc(ms).format(' W<br/>Y');
      },
    };
  } else {
    return {
      series: scoreByDeviceAndDate.map((x, i) => {
        return {
          name: x.device,
          data: getData(x.scoreByDate, 'month'),
          color: getSeriesColor(i),
        };
      }),
      tooltipDateFormatter: ms => moment.utc(ms).format('MMMM Y'),
      xAxisDateFormatter: ms => moment.utc(ms).format('MMM<br/>Y'),
    };
  }
}

const styles = {
  wrapper: {
    padding: 15,
    paddingBottom: 0,
  },
};

class ScoreByDeviceAndDateLineChart extends Component {
  render() {
    const intl = this.props.intl;
    const scoreTitle = intl.formatMessage({
      id: 'score',
      defaultMessage: 'Score',
    });

    const {
      series,
      tooltipDateFormatter,
      xAxisDateFormatter,
    } = getSeries(this.props.answersReport.scoreByDeviceAndDate, intl);
    
    const chartConfig = {
      chart: {
        height: this.props.height-(styles.wrapper.padding),
        width: this.props.width-(styles.wrapper.padding*2),
      },
      title: null,
      tooltip: {
        formatter: function() {
          const date = tooltipDateFormatter(this.x);
          const pointStrings = this.points.map(point => {
            const countMessage = intl.formatMessage({id: 'numberOfAnswers'}, {count: point.point.count});
            const score = ReactHighcharts.Highcharts.numberFormat(point.y, 1);
            return `
              <b style="color:${point.series.color}">${point.series.name}:</b>
              ${scoreTitle} ${score} (${countMessage})
            `;
          });
          return [date].concat(pointStrings);
        },
        split: true,
      },
      xAxis: {
        type: 'datetime',
        labels: {
          formatter: function() {
            return xAxisDateFormatter(this.value);
          },
          style: {
            color: grey500,
          },
        },
      },
      yAxis: {
        title: {
          text: scoreTitle,
          style: {
            color: grey500,
          },
        },
        min: 0,
        max: 10,
        tickInterval: 1,
      },
      series,
    };

    return (
      <div style={styles.wrapper}>
        <ReactHighcharts
          config={chartConfig}
          domProps={{id: this.props.uniqueId}}
        />
      </div>
    );
  }
}

export default createFragmentContainer(
  injectIntl(ScoreByDeviceAndDateLineChart),
  graphql`
    fragment ScoreByDeviceAndDateLineChart_answersReport on AnswersReport {
      scoreByDeviceAndDate {
        device
        scoreByDate {
          date
          score
          count
        }
      }
    }
  `,
);



// WEBPACK FOOTER //
// ./src/components/ScoreByDeviceAndDateLineChart/ScoreByDeviceAndDateLineChart.js